import { REQUIRED_FIELDS, VALIDATION_ERRORS } from './constants.js'

export class ConfigValidator {
  constructor(config) {
    this.config = config
    this.errors = []
  }

  async validate() {
    this.validateRequiredFields()
    this.validateTypes() 
    this.validateRanges()
    await this.validateFiles()

    if (this.errors.length > 0) {
      throw new Error(`Configuration validation failed:\n${this.errors.join('\n')}`)
    }

    return this.config
  }

  validateRequiredFields() {
    for (const field of REQUIRED_FIELDS) {
      if (!(field in this.config) || this.config[field] === null || this.config[field] === undefined) {
        this.errors.push(VALIDATION_ERRORS.MISSING_REQUIRED_FIELD(field))
      }
    }
  }

  validateTypes() {
    const typeValidations = [
      { field: 'yamlPath', type: 'string' },
      { field: 'casePath', type: 'string' },
      { field: 'boardPath', type: 'string' },
      { field: 'keyMap', type: 'object', isArray: true },
      { field: 'rows', type: 'number' },
      { field: 'columns', type: 'number' }
    ]

    for (const validation of typeValidations) {
      if (!(validation.field in this.config)) continue

      const value = this.config[validation.field]
      const actualType = typeof value
      
      if (validation.isArray) {
        if (!Array.isArray(value)) {
          this.errors.push(VALIDATION_ERRORS.INVALID_TYPE(validation.field, 'array', actualType))
        }
      } else if (actualType !== validation.type) {
        this.errors.push(VALIDATION_ERRORS.INVALID_TYPE(validation.field, validation.type, actualType))
      }
    }
  }

  validateRanges() {
    const rangeValidations = [
      { field: 'rows', min: 1, max: 10 },
      { field: 'columns', min: 1, max: 20 },
      { field: 'thumbKeys', min: 0, max: 6 }
    ]

    for (const validation of rangeValidations) {
      if (!(validation.field in this.config)) continue

      const value = this.config[validation.field]
      if (typeof value === 'number') {
        if (value < validation.min || value > validation.max) {
          this.errors.push(VALIDATION_ERRORS.INVALID_RANGE(
            validation.field, 
            validation.min, 
            validation.max, 
            value
          ))
        }
      }
    }

    if (Array.isArray(this.config.keyMap)) {
      const matrixKeys = this.config.rows * (this.config.columns * 2)
      
      // Auto-detect thumb keys if not provided
      let thumbKeys = this.config.thumbKeys
      if (thumbKeys === undefined || thumbKeys === null) {
        const remainingKeys = this.config.keyMap.length - matrixKeys
        if (remainingKeys < 0) {
          this.errors.push(`Keymap has fewer keys (${this.config.keyMap.length}) than matrix requires (${matrixKeys})`)
          return
        }
        // For split keyboards, thumb keys are already doubled in the keymap
        thumbKeys = remainingKeys
        this.config.thumbKeys = thumbKeys
      } else {
        // If thumbKeys was provided, it represents per-side count, so double it for split keyboard
        thumbKeys = thumbKeys * 2
        this.config.thumbKeys = thumbKeys
      }
      
      const expectedKeys = matrixKeys + thumbKeys
      
      if (this.config.keyMap.length !== expectedKeys) {
        this.errors.push(VALIDATION_ERRORS.INVALID_RANGE(
          'keyMap.length',
          expectedKeys,
          expectedKeys,
          this.config.keyMap.length
        ) + ` (Matrix: ${matrixKeys}, Thumbs: ${thumbKeys}, Total: ${expectedKeys})`)
      }
    }
  }

  async validateFiles() {
    const fileFields = ['yamlPath', 'casePath', 'boardPath']
    const validationPromises = fileFields.map(field => this.validateFile(field))
    
    if (this.config.rotationPath) {
      validationPromises.push(this.validateFile('rotationPath'))
    }

    await Promise.all(validationPromises)
  }

  async validateFile(field) {
    if (!(field in this.config)) return

    const path = this.config[field]
    
    try {
      const response = await fetch(path, { method: 'HEAD' })
      
      if (!response.ok) {
        this.errors.push(VALIDATION_ERRORS.NETWORK_ERROR(path, response.status))
      }
    } catch (error) {
      this.errors.push(VALIDATION_ERRORS.FILE_NOT_FOUND(path))
    }
  }
}